/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/navigation';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Button } from '@startengine/design-system.ui.button';
import { colors } from '@startengine/design-system.theme.main';
import ShareIcon from '@mui/icons-material/Share';
import { Modal } from '@startengine/design-system.ui.modal';
import useSnackbar from 'hooks/useSnackbar';
import { autoEmbedVideos } from './autoEmbedVideos';
import { getViewOpportunityOverride } from 'utils/offerings/updateOpportunityRedirects';

const UpdateModal = ({
  isOpen,
  onClose,
  updateData,
  isLoading = false,
  isMobile,
  StyledContentContainer,
}) => {
  const [_renderSnackbar, setSnackbar] = useSnackbar();
  const router = useRouter();

  const viewOpportunityOverride = getViewOpportunityOverride(updateData?.id);

  const handleViewOpportunity = () => {
    if (viewOpportunityOverride) {
      onClose();
      router.push(viewOpportunityOverride);
      return;
    }
    onClose();
  };

  const getShareText = () => {
    // Create a text preview from the content by removing HTML tags
    const contentPreview = updateData?.content
      ? `${updateData.content.replace(/<[^>]*>?/gm, '').substring(0, 100)}...`
      : 'Check out this campaign update!';

    return contentPreview;
  };

  const getShareUrl = () => {
    // Include metadata in the URL
    const baseUrl = `${window.location.href.split('#')[0]}?update=${updateData?.id}`;
    const title = encodeURIComponent(updateData?.title || 'Campaign Update');
    const text = encodeURIComponent(getShareText());

    return `${baseUrl}&title=${title}&text=${text}`;
  };

  const fallbackCopyToClipboard = (text) => {
    try {
      // Create a text area element and set its value to the given text
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);

      // Select the text
      textArea.select();

      // Copy the text to the clipboard
      const successful = document.execCommand('copy');

      // Remove the text area element
      document.body.removeChild(textArea);
      return successful;
    } catch (_) {
      // Handle error silently instead of logging to console
      return false;
    }
  };

  const copyToClipboard = () => {
    const shareUrl = getShareUrl();

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setSnackbar({
            message: 'Link copied to clipboard',
            severity: 'success',
          });
        })
        .catch(() => {
          // Use fallback method if clipboard API fails
          const result = fallbackCopyToClipboard(shareUrl);
          if (result) {
            setSnackbar({
              message: 'Link copied to clipboard',
              severity: 'success',
            });
          } else {
            setSnackbar({
              message: 'Failed to copy link',
              severity: 'error',
            });
          }
        });
    } else {
      // Use fallback method if clipboard API not available
      const result = fallbackCopyToClipboard(shareUrl);
      if (result) {
        setSnackbar({
          message: 'Link copied to clipboard',
          severity: 'success',
        });
      } else {
        setSnackbar({
          message: 'Failed to copy link',
          severity: 'error',
        });
      }
    }
  };

  const isShareDisabled = isLoading || !updateData?.id;

  const shareButton = (
    <Tooltip title={isShareDisabled ? '' : 'Copy Share Link'} arrow>
      <div
        role="button"
        tabIndex={isShareDisabled ? -1 : 0}
        aria-disabled={isShareDisabled}
        style={{
          cursor: isShareDisabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          borderRadius: '50%',
          minWidth: '40px',
          minHeight: '40px',
          opacity: isShareDisabled ? 0.4 : 1,
          pointerEvents: isShareDisabled ? 'none' : 'auto',
        }}
        aria-label="share"
        data-cy="share-update-modal-button"
        onClick={isShareDisabled ? undefined : copyToClipboard}
        onKeyDown={(e) => {
          if (isShareDisabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            copyToClipboard();
          }
        }}
      >
        <ShareIcon fontSize="small" />
      </div>
    </Tooltip>
  );

  return (
    <Modal
      fillHeight
      open={isOpen}
      height="lg"
      drawer={isMobile}
      data-testid="update-modal"
      data-cy="update-modal"
      actionButton={shareButton}
      sx={{
        '.modal-footer': { borderTop: `solid 1px ${colors.gray5}` },
        ...(isMobile && {
          '.MuiPaper-root': {
            height: '90vh',
            touchAction: 'pan-y',
            overflowY: 'auto',
          },
          '.modal-footer': { position: 'fixed', bottom: 0 },
        }),
      }}
      footer={
        <Grid container item xs={12} justifyContent="center">
          <Button
            data-cy="view-opportunity"
            variant="contained"
            color="primary"
            width="lg"
            onClick={handleViewOpportunity}
          >
            View Opportunity
          </Button>
        </Grid>
      }
      onClose={onClose}
    >
      {isLoading ? (
        <Grid
          container
          direction="column"
          spacing={2}
          data-testid="update-modal-skeleton"
          data-cy="update-modal-skeleton"
        >
          <Grid item>
            <Skeleton variant="text" width="60%" height={isMobile ? 32 : 48} />
          </Grid>
          <Grid item>
            <Skeleton variant="text" width="20%" height={20} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" width="100%" height={120} />
          </Grid>
          <Grid item>
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="92%" />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container justifyContent="space-between" alignItems="center">
            {updateData?.title && (
              <Typography variant={isMobile ? 'h5' : 'h3'}>{updateData.title}</Typography>
            )}
          </Grid>
          {updateData?.id && (
            <div data-testid="update-modal-id" style={{ display: 'none' }}>
              {updateData.id}
            </div>
          )}
          {updateData?.deployedAt && (
            <StyledContentContainer
              dangerouslySetInnerHTML={{
                __html: new Date(updateData?.deployedAt)
                  .toLocaleDateString('en-US', {
                    year: '2-digit',
                    month: 'numeric',
                    day: 'numeric',
                  })
                  .replace(/\//g, '.'),
              }}
            />
          )}
          <StyledContentContainer
            data-testid="update-modal-content"
            data-cy="update-modal-content"
            dangerouslySetInnerHTML={{
              __html: autoEmbedVideos(updateData?.content) || '<p>No content available.</p>',
            }}
          />
        </>
      )}
    </Modal>
  );
};

UpdateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  updateData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    deployedAt: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  StyledContentContainer: PropTypes.elementType.isRequired,
};

export default UpdateModal;
